import { usePathname, useRouter } from 'next/navigation';
import numbro from 'numbro';
import { useCallback, useContext } from 'react';

import * as Session from '@/lib/data/session';
import * as User from '@/lib/data/user';

import navigateToCustomerPortal from '@/lib/navigateToCustomerPortal';
import { isPresent } from 'shared/lib/utils';

import { StateContext } from '@/lib/StateProvider';

import authenticateThenRedirect from '@/lib/authenticateThenRedirect';
import createCheckoutSession from '@/lib/createCheckoutSession';

import { trackClick } from '@/lib/event_tracker';

export default function PricingTable({
  session,
  billingInterval,
  planButtonAsLink,
  planButtonText,
  planCostMessage,
  planFeatures,
  planName,
  planSubscriptionTier,
}) {
  const { dispatch } = useContext(StateContext);
  const router = useRouter();
  const pathname = usePathname();

  const authenticateThenRedirectCallback = useCallback(
    authenticateThenRedirect,
    [session]
  );

  const planId =
    planSubscriptionTier && planSubscriptionTier[billingInterval].id;

  const planButtonOnClick = useCallback(
    (skipAuthentication) => (_e) => {
      trackClick(session, pathname, 'Subscribe To Plan Button', {
        billingInterval,
        planId,
        planType: 'paid',
      });

      if (skipAuthentication) {
        return;
      }

      if (!Session.isAuthenticated(session)) {
        authenticateThenRedirectCallback(session, pathname, dispatch, router, {
          paidPlanSubscriberPath: `${pathname}?customer-portal=true`,
          freePlanSubscriberPath: `${pathname}?checkout=${planId}`,
        });

        return;
      }

      if (User.isPaidPlanSubscriber(session.get('user'))) {
        navigateToCustomerPortal(dispatch, session, pathname);
        return;
      }

      createCheckoutSession(dispatch, session, pathname, planId);
    },
    [planId, session]
  );

  function formatPrice(billingInterval, planSubscriptionTier, price) {
    return billingInterval === 'yearly'
      ? numbro(planSubscriptionTier.yearly[price] / 100 / 12).format({
          optionalMantissa: true,
          thousandSeparated: true,
          mantissa: 2,
        })
      : planSubscriptionTier.monthly[price] / 100;
  }

  return (
    <div className="flex flex-col rounded-md border border-gray-200 p-8 shadow-lg">
      <h3 className="text-center text-2xl font-semibold tracking-tight">
        {planName}
      </h3>

      <div className="flex h-44 flex-col justify-center">
        {(() => {
          if (isPresent(planCostMessage)) {
            return (
              <div className="grid text-center">
                <p className="text-2xl font-semibold tracking-tight text-gray-600">
                  {planCostMessage}
                </p>
              </div>
            );
          }

          const planPrice = formatPrice(
            billingInterval,
            planSubscriptionTier,
            'priceInCents'
          );

          const planMarkdown = formatPrice(
            billingInterval,
            planSubscriptionTier,
            'priceMarkdownInCents'
          );

          const planBillingIntervalDetail =
            billingInterval === 'yearly'
              ? `per month ($${
                  planSubscriptionTier.yearly.priceInCents / 100
                } per year)`
              : 'per month';

          return (
            <div className="flex flex-col items-center text-center">
              <span className="flex flex-col text-center">
                <span className="text-gray-400 line-through">
                  ${planMarkdown}
                </span>

                <span>
                  <span className="align-top text-2xl text-gray-400">$</span>
                  <span className="text-6xl font-bold tracking-tight">
                    {planPrice}
                  </span>
                </span>
              </span>
              <span className="font-light">{planBillingIntervalDetail}</span>
            </div>
          );
        })()}
      </div>

      <div className="">
        {(() => {
          if (isPresent(planButtonAsLink)) {
            return (
              <a
                href={planButtonAsLink}
                className="mb-2 mr-2 w-full rounded bg-gray-800 px-5 py-2.5 text-center text-lg font-medium text-white hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700"
                type="button"
                onClick={planButtonOnClick(true)}
              >
                {planButtonText}
              </a>
            );
          }

          return (
            <button
              className="mb-2 mr-2 w-full rounded bg-gray-800 px-5 py-2.5 text-lg font-medium text-white hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700"
              type="button"
              onClick={planButtonOnClick(false)}
            >
              {planButtonText}
            </button>
          );
        })()}

        <ul className="mt-8 flex flex-col space-y-2">
          {planFeatures.map(({ text, Icon }, idx) => {
            return (
              <li key={idx}>
                <span className="flex items-center space-x-3 sm:text-lg">
                  <p>
                    <Icon />
                  </p>

                  <p>{text}</p>
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
